// parent-picker.js — data rules for the task pickers that search the project on demand: the
// task drawer's Change Parent picker (TPT618, components/task-detail.js showParentPicker()) and
// the New Task modal's Depends on typeahead (TPT598, components/dependency-typeahead.js). Kept
// pure (no DOM, no api.js) so it is unit testable the same way lib/task-events-seen.js is.
//
// Candidates come from the server, never from the drawer's options.allTasks: a caller's rows
// are a filtered board page, one epic's children, or — on the standalone task page — only the
// open task's own children. Each query is one bounded summary page of GET /tasks, the same
// shape the Trello import's task search uses (fields=summary&q=&limit=).

// Rows per query. The list route caps limit at 500; 50 keeps a picker reply small.
export const PARENT_PICKER_LIMIT = 50;

// Path (for api.get) of one candidate page for `query`. A blank query lists the first page in
// the list route's own order (newest sprint first); otherwise the API matches the trimmed text
// against task_key, title or description. The search input carries maxlength="200", the API's
// q cap, so a longer value never reaches the route's 400.
export function taskSearchPath(projectId, query) {
  const params = new URLSearchParams({
    fields: 'summary',
    limit: String(PARENT_PICKER_LIMIT),
    include_children_count: 'false',
    include_active_device: 'false',
  });
  const q = String(query ?? '').trim();
  if (q) params.set('q', q);
  return `/projects/${projectId}/tasks?${params}`;
}

export const parentPickerPath = taskSearchPath;

// Rows `task` may be moved under: everything except the task itself and its direct children.
// Read off each candidate's own parent_id, so the exclusion never depends on which rows the
// caller happened to hold.
export function parentPickerCandidates(rows, task) {
  return (rows || []).filter(t => t.id !== task.id && t.parent_id !== task.id);
}

// Rows offered as new dependencies: everything with a task key not already chosen. The New Task
// modal's task does not exist yet, so nothing else can be excluded.
export function dependencyCandidates(rows, selectedKeys) {
  const selected = new Set(selectedKeys || []);
  return (rows || []).filter(t => t && t.task_key && !selected.has(t.task_key));
}
